import { Trans } from '@lingui/react';
import { Link } from '@startuptools/ui/Link';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Radio } from '@startuptools/ui/Radio';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { useWpSetStrikePriceMutation } from '../../../../../graphql/react-operations';
import { BigNumber } from 'bignumber.js';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { GqlCompany, GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';

type FormValues = {
  useQuotaValue: 'true' | 'false';
  strikePrice: string;
};

export const QesoStepSharePrice = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'companyId' | 'strikePriceFromQuotaValue' | 'strikePrice'> & {
    company: Pick<GqlCompany, 'sharesTotal' | 'shareCapital'>;
  };
}) => {
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const defaultQuotaValue = new BigNumber(warrantProgram.company.shareCapital)
    .div(warrantProgram.company.sharesTotal)
    .decimalPlaces(6);

  const [doSetStrikePrice, { loading }] = useWpSetStrikePriceMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      useQuotaValue: `${warrantProgram.strikePriceFromQuotaValue ?? true}`,
      strikePrice: warrantProgram.strikePrice ?? defaultQuotaValue.toString(),
    },
  });

  const useQuotaValue = watch('useQuotaValue') === 'true';

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSetStrikePrice({
        variables: {
          companyId: warrantProgram.companyId,
          input: {
            id: warrantProgram.id,
            strikePriceFromQuotaValue: values.useQuotaValue === 'true',
            strikePrice: values.strikePrice,
            useNetStrike: false,
          },
        },
      });
    } catch (e) {
      captureException(e);
      toast({
        severity: 'danger',
        title: <Trans id="Could not save share price" />,
        description: (
          <Trans id="Could not save share price, please try again and contact support if problem persists" />
        ),
      });
    }
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="What should the price per share be?" />
        </Typography>
        <Alert>
          <Typography component="span" level="title-sm">
            <Trans
              id="When the vesting period of the option has been completed, the options holder will have the right to subscribe for shares. The lowest price per share you can set is the quota value, that is, the company's share capital divided by the number of shares: <link>read more</link>"
              components={{
                link: (
                  <Link
                    href="https://startuptools.org/se/vilken-teckningskurs-ska-man-satta-pa-en-kvalificerad-personaloption/"
                    target="_href"
                  />
                ),
              }}
            />
          </Typography>
        </Alert>

        <Typography level="title-md">
          <Trans id="Shall the price per share be the quota value?" />
        </Typography>

        <Controller
          control={control}
          name="useQuotaValue"
          render={({ field }) => (
            <RadioGroup {...field}>
              <Radio value label={<Trans id="Yes" />} />
              <Radio value={false} label={<Trans id="No, use another price" />} />
            </RadioGroup>
          )}
        />

        <Controller
          control={control}
          name="strikePrice"
          render={({ field }) => (
            <FormControl sx={{ maxWidth: 300 }} required>
              <FormLabel>
                {useQuotaValue ? <Trans id="State the quota value" /> : <Trans id="State price per share" />}
              </FormLabel>
              <NumericFormatInput {...field} required />
            </FormControl>
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
